import * as React from 'react';

import { Action, ActionMenu } from '~shared/components/ActionMenu';
import { IconChevron } from '~shared/components/icons/micro';

import {
  WorkspaceMember,
  WorkspaceRole,
} from '../../../../../api/workspaces/getWorkspaceMembers';

interface Props {
  member: WorkspaceMember;
  roleLabel: React.ReactNode;
  // The existing action callbacks consume `data` as a single member rather
  // than an array (despite ActionMenu's typed signature), so we keep the
  // generic loose to mirror how the table's other consumers wire this up.
  actions: Action[];
  canRemoveOthers: boolean;
  currentUserId: string;
  currentUserRole: WorkspaceRole;
}

const checkIsRowCurrentUser = ({
  rowUserId,
  currentUserId,
}: {
  rowUserId: string;
  currentUserId: string;
}) => rowUserId === currentUserId;

const checkIsCurrentUserOwner = (currentUserRole: WorkspaceRole) =>
  currentUserRole === WorkspaceRole.Owner;

const checkCanShowActionsMenu = ({
  currentUserId,
  rowUserId,
  rowUserRole,
  canRemoveOthers,
  currentUserRole,
}: {
  currentUserId: string;
  rowUserId: string;
  rowUserRole: WorkspaceRole;
  canRemoveOthers: boolean;
  currentUserRole: WorkspaceRole;
}) => {
  const isRowCurrentUser = checkIsRowCurrentUser({
    rowUserId,
    currentUserId,
  });

  const isCurrentUserOwner = checkIsCurrentUserOwner(currentUserRole);

  if (isRowCurrentUser && isCurrentUserOwner) {
    return false;
  }

  if (isRowCurrentUser && !isCurrentUserOwner) {
    return true;
  }

  // Owner row can never be acted on by anyone else; ownership only changes via
  // the dedicated transfer-ownership flow.
  if (rowUserRole === WorkspaceRole.Owner) {
    return false;
  }

  return canRemoveOthers;
};

export function WorkspaceMembersTableAccessMenu({
  member,
  roleLabel,
  actions,
  canRemoveOthers,
  currentUserId,
  currentUserRole,
}: Props) {
  const canShowActionsMenu = checkCanShowActionsMenu({
    currentUserRole,
    currentUserId,
    canRemoveOthers,
    rowUserId: member.id,
    rowUserRole: member.role,
  });

  return (
    <ActionMenu
      actions={actions}
      data={member as unknown as object[]}
      isDisabled={!canShowActionsMenu}
    >
      <div
        className={`flex flex-row items-center justify-center gap-2 ${
          canShowActionsMenu ? 'cursor-pointer' : ''
        }`}
      >
        {roleLabel}
        {canShowActionsMenu ? <IconChevron direction={'down'} /> : null}
      </div>
    </ActionMenu>
  );
}
